<template>
  <div class="product-form">
    <form
      class="product-form__form"
      @submit.prevent="handleAddToCart"
    >
      <div class="form-group">
        <!-- Product linking swatches for standard template -->
        <div
          v-if="swatchProducts.length > 0 && !isGiftCardTemplate"
          :id="`form-group-id-${product.id}`"
          class="form-group__field product-form__options"
        >
          <product-swatches
            :swatch-products="swatchProducts"
            :product-link="true"
            :option="{
              name: product.linkedName?.value
            }"
            :selected-linked-options=" {
              value: product.title
            }"
            :linked-name="product.linkedName"
            @linked-option-change="handleLinkedOptionChange"
          />
        </div>

        <div
          v-show="!hasOnlyDefaultVariant"
          :id="`form-group-option-${product.id}`"
          class="form-group__field product-form__options"
        >
          <product-swatches
            v-for="(option, index) in product.options"
            :key="`${option.name}-${option.index}`"
            :option="option"
            :product="product"
            :selected-options="selectedOptions"
            :show-swatch-error="showSwatchError"
            :force-update-options="forceUpdateOptions"
            :gift-card-option-desc="index === 0 ? giftCardOptionDesc : null"
            @option-change="handleOptionChange"
          />
        </div>

        <!-- Product linking swatches for gift card template -->
        <div
          v-if="swatchProducts.length > 0 && isGiftCardTemplate"
          :id="`form-group-id-${product.id}`"
          class="form-group__field product-form__options"
        >
          <product-swatches
            :swatch-products="swatchProducts"
            :product-link="true"
            :option="{
              name: product.linkedName?.value
            }"
            :selected-linked-options=" {
              value: product.title
            }"
            :linked-name="product.linkedName"
            @linked-option-change="handleLinkedOptionChange"
          />
        </div>

        <div
          v-if="product.hasSubscription"
          id="rc_container"
          class="product-form__subscription-options"
          :class="[
            product.subscriptionOnly ? 'rc-subscription-only' : '',
            recharge.singleVariant ? recharge.single_variant : ''
          ]"
        >
          <input
            id="rc_shipping_interval_unit_type"
            ref="shippingIntervalUnitType"
            type="hidden"
            name="properties[shipping_interval_unit_type]"
            :value="product.shippingIntervalUnitType?.value"
          >
          <div
            class="product-form__tooltip-wrapper"
          >
            <label
              v-if="recharge.labelDeliverEvery"
              class="
                product-form__label
                text-label-xs
                text-label-s-desktop
              "
              :class="[
                product.subscriptionOnly ? 'rc-subscription-only' : '',
                product.subscriptionOnly && recharge.singleVariant ? recharge.single_variant : ''
              ]"
              v-html="recharge.labelDeliverEvery"
            />
            <div
              v-if="recharge.showWidget"
              class="product-form__subscription-tooltip"
            >
              <img
                v-if="recharge.showWidgetIcon"
                :src="recharge.widgetIcon"
                height="21"
                width="18"
                alt="ReCharge Subscription services icon"
                class="product-form__subscription-icon"
                @click="handleOpenTooltip"
              >
              <span
                v-if="recharge.showSubscriptionDetails"
                class="text-body-xs text-body-s-desktop"
                @click="handleOpenTooltip"
                v-html="recharge.subscriptionDetailsLabel"
              />

              <div
                v-if="showTooltip"
                class="product-form__tooltip"
              >
                <div class="text-body-s">
                  <button
                    class="button product-form__tooltip-close"
                    @click="handleCloseTooltip"
                  >
                    &times;
                  </button>

                  <p v-html="howItWorksText" />

                  <a
                    v-if="recharge.showLearnmore"
                    :href="recharge.learnmoreUrl"
                    target="_blank"
                    aria-label="Learn more about ReCharge Payments"
                    v-html="recharge.learnMoreLink"
                  />
                </div>

                <div
                  v-if="recharge.showPoweredby"
                  class="product-form__tooltip-footer"
                >
                  <a
                    :href="recharge.poweredbyUrl"
                    target="_blank"
                    aria-label="ReCharge Payments homepage"
                  >
                    <img
                      src="//rechargestatic-bootstrapheroes.netdna-ssl.com/static/images/widget/rc_widget__banner@2x.png"
                      height="28"
                      width="153"
                      alt="ReCharge Subscriptions company banner"
                      class="rc_popup__badge"
                    >
                  </a>
                </div>
              </div>
            </div>

            <a
              v-if="recharge.showWidget === 'link'"
              :href="recharge.learnmoreUrl"
              target="_blank"
              class="rc_popup__hover"
              aria-label="Learn more about ReCharge Payments"
            >
              <img
                v-if="rechage.showWidgetIcon"
                src="{{ recharge.widgetIcon }}"
                height="21"
                width="18"
                alt="ReCharge Subscription services icon"
                class="rc_popup__icon"
              >

              {{ recharge.subscriptionDetailsLabel }}
            </a>
          </div>

          <div
            id="rc_radio_options"
            class="product-form__group"
            :class="[
              recharge.firstOption === 'autodeliver' ? 'rc_everse-options' : ''
            ]"
            role="radiogroup"
            aria-labelledby="Purchase options"
          >
            <!-- one time -->
            <div
              v-if="product.subscriptionOnly.value === 'false'"
              class="
                rc_block
                rc_block__type
                rc_block__type__onetime
              "
              :class="[
                !recharge.subscriptionFirst ? 'rc_block__type--active' : ''
              ]"
            >
              <div class="product-form__radio-field">
                <input
                  id="rc_purchase_type_onetime"
                  v-model="purchaseType"
                  type="radio"
                  name="purchase_type"
                  value="onetime"
                  class="rc_radio rc_radio__onetime product-form__radio"
                  :checked="recharge.selectSubscriptionFirst.value === 'false'"
                >
                <label
                  for="rc_purchase_type_onetime"
                  class="rc_label__onetime product-form__label text-body-s"
                >
                  {{ recharge.labelOnetime }}
                  <span
                    v-if="recharge.discountPercentage > 0"
                    id="rc_price_onetime"
                    class="rc_price rc_price__onetime"
                    aria-label="original price"
                  />
                </label>
              </div>
            </div>
            <!-- one time ends -->

            <!-- autodeliver -->
            <div
              v-if="!expiryOptions"
              class="rc_block__type__autodeliver"
              :class="[
                recharge.selectSubscriptionFirst === true ? 'rc_block__type--active' : '',
              ]"
            >
              <div
                class="product-form__radio-field"
                :class="[
                  product.subscriptionOnly.value === 'true' ? 'is-hidden' : ''
                ]"
              >
                <input
                  id="rc_purchase_type_autodeliver"
                  v-model="purchaseType"
                  type="radio"
                  name="purchase_type"
                  value="autodeliver"
                  :checked="recharge.selectSubscriptionFirst === true"
                  class="rc_radio rc_radio__autodeliver product-form__radio"
                >
                <label
                  for="rc_purchase_type_autodeliver"
                  class="rc_label__autodeliver product-form__label text-body-s"
                >
                  <span v-html="recharge.labelAutodeliver" />
                  <span
                    v-if="recharge.discountPercentage > 0"
                    class="rc_label__discount"
                    aria-label="subscription discount"
                  >({{ discount_percentage }}%):</span>
                  <span
                    id="rc_price_autodeliver"
                    class="rc_price rc_price__autodeliver"
                    aria-label="recurring price"
                  />
                </label>
              </div>

              <div
                v-show="purchaseType === 'autodeliver'"
                id="rc_autodeliver_options"
                class="rc_block rc_block__type__options"
              >
                <label
                  for="rc_shipping_interval_frequency"
                  class="rc_label rc_label__deliver_every product-form__label"
                  aria-label="Subscription frequency options"
                >
                  <span
                    v-if="
                      shippingIntervalFrequency.length === 1 &&
                        recharge.labelUnitTypeLegacy === 'Months' ||
                        shippingIntervalFrequency.length === 1 &&
                        recharge.labelUnitTypeLegacy === 'Weeks'
                    "
                    class="rc_label__frequency visually-hidden label-xs label-s-desktop"
                  >
                    {{ frequencyLabel() }}
                  </span>
                  <select
                    id="rc_shipping_interval_frequency"
                    ref="shippingIntervalFrequency"
                    name="properties[shipping_interval_frequency]"
                    class="product-form__select"
                    :class="[
                      recharge.hideFrequencyClass ? recharge.hideFrequencyClass : ''
                    ]"
                    :style="'background-image: url({{ this.recharge. caraeUrl }});'"
                  >
                    <option
                      v-for="(frequency, index) in shippingIntervalFrequency"
                      :key="index"
                      :value="frequency"
                    >
                      {{ frequency }}
                      {{ frequencyLabel(frequency) }}
                    </option>
                  </select>
                </label>
              </div>
            </div>
            <!-- autodeliver ends -->

            <!-- temporary -->
            <div
              v-if="expiryOptions"
              class="rc_block__type__temporary"
              :class="[
                recharge.selectSubscriptionFirst === true ? 'rc_block__type--active' : '',
                expiryOptions && expiryOptions.length >= 1 ? '' : 'is-hidden'
              ]"
            >
              <div
                :class="[
                  expiryOptions && product.subscriptionOnly.value === 'true' ? 'is-hidden' : ''
                ]"
                class="product-form__radio-field"
              >
                <input
                  id="rc_purchase_type_autodeliver"
                  v-model="purchaseType"
                  type="radio"
                  name="purchase_type"
                  value="temporary"
                  :checked="recharge.selectSubscriptionFirst === true"
                  class="rc_radio rc_radio__temporary product-form__radio"
                  :class="[
                    product.subscriptionOnly.value === 'true' ? 'is-hidden' : ''
                  ]"
                >
                <label
                  v-if="product.subscriptionOnly.value === 'false'"
                  for="rc_purchase_type_autodeliver"
                  class="rc_label__temporary product-form__label text-body-s"
                >
                  <span
                    v-if="expiryOptions.length === 1"
                  >
                    {{ expiryOptions[0] }} {{ $string('product.subscribe.frequency') }} {{ $string('product.subscribe.temporary_membership') }}
                  </span>

                  <span
                    v-else
                    v-html="'Temporary Membership'"
                  />

                  <span
                    id="rc_price_autodeliver"
                    class="rc_price rc_price__temporary"
                    aria-label="recurring price"
                  />
                </label>
              </div>

              <div
                v-show="purchaseType === 'temporary'"
                id="rc_autodeliver_options"
                class="rc_block rc_block__type__options"
              >
                <input
                  ref="shippingIntervalFrequency"
                  type="hidden"
                  name="properties[shipping_interval_frequency]"
                  value="1"
                >

                <label
                  for="rc_shipping_interval_frequency"
                  class="rc_label rc_label__deliver_every product-form__label"
                  aria-label="Subscription frequency options"
                >
                  <span
                    v-if="
                      shippingIntervalFrequency.length === 1 &&
                        recharge.labelUnitTypeLegacy === 'Months' ||
                        shippingIntervalFrequency.length === 1 &&
                        recharge.labelUnitTypeLegacy === 'Weeks'
                    "
                    class="rc_label__frequency visually-hidden label-xs label-s-desktop"
                  >
                    {{ frequencyLabel() }}
                  </span>

                  <select
                    id="rc_shipping_interval_frequency"
                    ref="shippingExpiration"
                    name="properties[number_charges_until_expiration]"
                    class="product-form__select"
                    :class="[
                      recharge.hideFrequencyClass ? recharge.hideFrequencyClass : ''
                    ]"
                    :style="'background-image: url({{ this.recharge. caraeUrl }});'"
                  >
                    <option
                      v-for="(frequency, index) in expiryOptions"
                      :key="index"
                      :value="frequency"
                    >
                      <span
                        v-if="product.giftProduct.value === 'true'"
                        v-html="'Gift'"
                      />
                      {{ frequency }} {{ $string('product.subscribe.shipments') }} &dash;
                      <product-prices
                        :available="variant?.available"
                        block-class="main-product"
                        :compare-at-price="variant?.compareAtPrice?.amount"
                        :element="'span'"
                        :hide-oos="true"
                        :price="variant?.price?.amount || liquid.price"
                      />
                      {{ $string('product.subscribe.per_shipment') }}
                    </option>
                  </select>
                </label>
              </div>
            </div>
            <!-- temporary ends -->
          </div>
        </div>

        <div v-if="enableGiftMessaging">
          <div class="product-form__message-toggle checkbox">
            <input
              id="addMessage"
              v-model="hasGiftMessage"
              name="properties[gift_message]"
              type="checkbox"
              class="checkbox__input"
            >

            <label
              class="checkbox__label text-body-s"
              for="addMessage"
              v-text="$string('product.gift_message.add_gift_message')"
            />
          </div>

          <GiftMessage
            v-if="hasGiftMessage"
            :has-gift-message="hasGiftMessage"
            @validate-gift-message="handleGiftMessage"
          />
        </div>

        <div class="form-group__footer">
          <btn
            class="main-product__add-to-cart"
            :class="[
              {'disabled': (state === 'out of stock')},
              {'Rise-add-to-cart-button': isDigitalGiftProduct}
            ]"
            block-class="main-product"
            :label="addToCartText"
            modifiers="slate large"
            :show-state-icon="state === 'adding'"
            :show-icon="false"
            style="margin-right: 0; margin-bottom: 0;"
            :style="{display: displayAtc}"
            type="submit"
          >
            <template #labelPrefix>
              <product-prices
                :available="variant?.available"
                block-class="main-product"
                :compare-at-price="variant?.compareAtPrice?.amount"
                :element="'span'"
                :hide-oos="true"
                :price="variant?.price?.amount || liquid.price"
              />
              <span>&nbsp;&nbsp;&nbsp;–&nbsp;&nbsp;&nbsp;</span>
            </template>
            <template #stateIcon>
              <icon-loading />
            </template>
          </btn>

          <btn
            v-if="isGiftProductType"
            v-show="isDigitalGiftProduct"
            class="main-product__add-to-cart gwbutton"
            block-class="main-product"
            modifiers="slate large"
            :label="$string('cart.gift.send_as_a_gift')"
            :show-icon="false"
            type="submit"
          >
            <template #labelPrefix>
              <product-prices
                :available="variant?.available"
                block-class="main-product"
                :compare-at-price="variant?.compareAtPrice?.amount"
                :element="'span'"
                :hide-oos="true"
                :price="variant?.price?.amount || liquid.price"
              />
              <span>&nbsp;&nbsp;&nbsp;–&nbsp;&nbsp;&nbsp;</span>
            </template>
            <template #stateIcon>
              <icon-loading />
            </template>
          </btn>
        </div>
      </div>
    </form>

    <p
      v-if="error"
      class="main-product__error text-body-m"
      v-text="error"
    />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 */
import { mapActions } from 'vuex'

import Btn from '~global/btn/btn'
import ProductSwatches from '~async/product-form/product-swatches'
import ProductPrices from '~global/product-prices/product-prices'
import GiftMessage from '~async/gift-message/gift-message'
import IconLoading from '~icons/misc/loading.svg'
import timings from '~/config/timings'
import { convertFirstLetterCapital } from '~/helpers/convert'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    IconLoading,
    ProductSwatches,
    ProductPrices,
    GiftMessage,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        hasOnlyDefaultVariant: false,
      }),
    },
    recharge: {
      type: Object,
      default: () => ({
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    swatchProducts: {
      type: [Boolean, Object],
      default: false,
    },
    canHavePromoItem: {
      type: [Boolean],
      default: false,
    },
    promoItem: {
      type: [Number],
      default: null,
    },
    enableGiftMessaging: {
      type: Boolean,
      default: false,
    },
    isGiftCardTemplate: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update-linked-variant',
    'update-variant',
    'update-promo-item',
  ],

  data() {
    return {
      currentVariantId: this.variant?.id || 0,
      lineItemProperties: {},
      error: '',
      hasGiftMessage: false,
      quantity: 1,
      state: 'ready',
      timeout: {},
      showSwatchError: false,
      selectedOptions: [],
      priceLabel: '',
      forceUpdateOptions: false,
      purchaseType: this.recharge.selectSubscriptionFirst ? 'autodeliver' : 'onetime',
      showTooltip: false,
      hasFormErrors: false,
      giftCardOptionDesc: null,
      giftMessage: {
        to: this.liquid.cart.attributes.to,
        from: this.liquid.cart.attributes.from,
        message: this.liquid.cart.attributes.message,
      },
      expiryOptions: false,
    }
  },

  computed: {

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      const atcText = this.state === 'out of stock'
        ? this.$string('product.form.sold_out') || 'Out of Stock'
        : this.$string('product.form.add_to_cart') || 'Add to Cart'

      return atcText
    },

    /**
     * Compute has only default variant from Liquid and product objects.
     * @returns {Boolean}
     */
    hasOnlyDefaultVariant() {
      return this.liquid.hasOnlyDefaultVariant || this.product.hasOnlyDefaultVariant
    },

    howItWorksText() {
      return this.recharge.howItWorksText
    },

    /**
     * Compute if product type is a digital gift card.
     * @return {boolean}
     */
    isDigitalGiftProduct() {
      return this.product.giftCard
    },

    /**
     * Compute if product type is any gift card.
     * @return {boolean}
     */
    isGiftProductType() {
      if (!this.product.type) {
        return false
      }

      return this.product.type.toLowerCase().includes('gift card')
    },

    /**
     * Compute main add to cart button display for gift card products.
     * @return {String}
     */
    displayAtc() {
      return this.isDigitalGiftProduct ? 'none' : 'flex'
    },
  },

  watch: {

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromId(this.currentVariantId)

      if (!variant) {
        return
      }

      this.$emit('update-variant', variant)
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product() {
      this.getCurrentVariant()
      this.updateProductMeta()
      this.purchaseType = this.recharge.selectSubscriptionFirst ? 'autodeliver' : 'onetime'
      this.subscriptionExpiryOptions()
      this.getShippingIntervalFrequency()
    },

    /**
     * Watch for variant change and handle update
     * - If there is a current variant set - update the `selectedOptions` data
     *   to match the new variant's `selectedOptions`
     * - If there are selected options set, but the new variant is set to `false`
     *   it means the options combination does not match a variant so the last
     *   selected option is reset to false.
     */
    variant() {
      this.error = ''
      this.showSwatchError = false

      if (this.variant) {
        const selectedOptions = [...this.variant.selectedOptions]
        this.selectedOptions = selectedOptions
        this.giftCardOptionDesc = this.variant.giftCardOptionDesc || null
        this.state = this.variant.available ? 'ready' : 'out of stock'
      } else if (this.selectedOptions.length) {
        this.selectedOptions[this.lastChangedOptionIndex] = false
      }
    },
  },

  mounted() {
    this.getCurrentVariant()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      updateCartAttributes: 'cart/updateCartAttributes',
      clearCartAttributes: 'cart/clearCartAttributes',
      openOverlay: 'overlays/open',
    }),

    /**
     * Get the shipping interval frequency
     */
    getShippingIntervalFrequency() {
      if (!this.product.shippingIntervalFrequency) {
        this.shippingIntervalFrequency = this.product.shippingIntervalFrequency
        return
      }

      this.shippingIntervalFrequency = this.product.shippingIntervalFrequency.value.split(',')
    },

    /**
     * Handle frequency string output
     * @param {String} frequency - Index of option that has changed
     * @returns {String}
     */
    frequencyLabel(frequency) {
      if (!this.product.shippingIntervalUnitType?.value) {
        return
      }

      if (frequency === '1') {
        return convertFirstLetterCapital(this.product.shippingIntervalUnitType?.value)
      }

      return convertFirstLetterCapital(`${this.product.shippingIntervalUnitType?.value}s`)
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (
        this.variant.id === this.currentVariantId ||
        !this.product ||
        !this.product.variants.length
      ) {
        return
      }

      if (this.hasOnlyDefaultVariant) {
        this.currentVariantId = this.product.variants[0].id
        return
      }

      /**
       * Pick the first in stock variant.
       * - If no variants are in stock use first.
       * - Or use variant from URL if set.
       */
      const inStockVariants = this.product.variants.filter((variant) => {
        return variant.available
      })

      const defaultVariant = inStockVariants.length
        ? inStockVariants[0]
        : this.product.variants[0]

      const variant = this.getVariantFromUrl() || defaultVariant

      this.currentVariantId = variant.id
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromId(variantId)
    },

    /**
     * Get variant object from variant ID.
     * @returns {Object}
     */
    getVariantFromId(variantId) {
      return this.product.variants.find((variant) => {
        return variant.id === variantId
      })
    },

    /**
     * Get variant title.
     * @param {Object} variant - Variant object.
     * @returns {String}
     */
    getVariantTitle(variant) {
      return variant.available
        ? variant.title
        : `${variant.title} - ${this.$string('product.form.sold_out')}`
    },

    handleGiftMessage(data){
      if (data.errors) {
        return
      }
      this.hasFormErrors = data.errors
      this.giftMessage = data
    },

    /**
     * Handle option change from product swatch
     * - Updates the selected product object,
     * - Finds a matching product from the new options object
     * - Emits a product change
     * @param {Object} data - Option data
     */
    handleLinkedOptionChange(data) {
      this.$emit('update-linked-variant', data)
    },

    /**
     * Handle option change from product swatch
     * - Updates the `selectedOptions` object,
     * - Finds a matching variant from the new options object
     * - Emits a variant change
     * @param {Number} index - Index of option that has changed
     * @param {Object} data - Option data
     * @param {String} data.name - Option name
     * @param {String} data.value - Option value
     */
    handleOptionChange(index, data) {
      this.lastChangedOptionIndex = index
      this.selectedOptions[index] = data

      const newVariant = this.product.variants.find((variant) => {
        const variantOptionString = JSON.stringify(variant.selectedOptions)
        const newOptionString = JSON.stringify(this.selectedOptions)
        return variantOptionString === newOptionString
      })

      if (newVariant) {
        this.$emit('update-variant', newVariant)

        if (!newVariant.available) {
          this.state = 'out of stock'
        } else if (this.variant?.id === newVariant.id) {
          this.state = 'ready'
        }
      } else {
        this.forceUpdateOptions = !this.forceUpdateOptions
        this.state = 'out of stock'
      }
    },

    /**
     * Handle quantity selector update.
     * @param {String} quantity - New quantity.
     */
    handleQuantityUpdate(quantity) {
      this.quantity = quantity
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart(event) {
      if (!this.variant || this.state !== 'ready') {
        return
      }

      this.lineItemProperties = {}

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (cnvs.storybook) {
        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, timings.slow)

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, timings.message)

        return
      }

      if (this.purchaseType === 'autodeliver' && this.$refs.shippingIntervalUnitType) {
        this.lineItemProperties = {
          ...this.lineItemProperties,
          shipping_interval_unit_type: this.$refs.shippingIntervalUnitType.value,
        }
      }

      if (this.purchaseType === 'autodeliver' && this.$refs.shippingIntervalFrequency) {
        this.lineItemProperties = {
          ...this.lineItemProperties,
          shipping_interval_frequency: this.$refs.shippingIntervalFrequency.value,
        }
      }

      if (this.purchaseType === 'temporary' && this.$refs.shippingIntervalFrequency) {
        this.lineItemProperties = {
          ...this.lineItemProperties,
          shipping_interval_frequency: this.$refs.shippingIntervalFrequency.value,
          number_charges_until_expiration: this.$refs.shippingExpiration.value,
        }
      }

      if (this.hasGiftMessage && !this.hasFormErrors) {
        this.lineItemProperties = {
          ...this.lineItemProperties,
          gift_message_to: this.giftMessage.to,
          gift_message_from: this.giftMessage.from,
        }
      }

      if (this.hasGiftMessage && this.giftMessage.message?.length > 0) {
        this.lineItemProperties = {
          ...this.lineItemProperties,
          gift_message_to: this.giftMessage.to,
          gift_message_from: this.giftMessage.from,
          gift_message_message: this.giftMessage.message,
        }
      }

      const items = [
        {
          id: this.variant.id,
          properties: {
            ...this.lineItemProperties,
          },
          quantity: this.quantity,
        },
      ]

      if (this.canHavePromoItem) {
        this.$emit('update-promo-item', false)

        items[0].properties = {
          ...this.lineItemProperties,
          promoProduct: true,
        }
        items.unshift({
          id: this.liquid.promoItem,
          quantity: 1,
        })
      }

      try {
        await this.addItem(items)

        this.openOverlay({
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        })

        this.state = 'added'

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, timings.message)

      } catch (error) {
        this.state = 'error'

        if (error.length) {
          this.error = error
          return
        }

        reportError('Failed to add product to cart', error)
      }
    },

    handleOpenTooltip(event) {
      event.preventDefault()
      this.showTooltip = true
    },

    handleCloseTooltip(event) {
      event.preventDefault()
      this.showTooltip = false
    },

    /**
     * Force updates product metadata in global object - window.ShopifyAnalytics
     */
    updateProductMeta() {
      if (this.product.id === window.ShopifyAnalytics.meta.page.resourceId) {
        return
      }

      const currentVariants = this.product.variants.map((variant) => {
        return {
          id: variant.id,
          name: `${this.product.title} - ${variant.selectedOptions[0].value || ''}`,
          price: variant.price.amount,
          public_title: variant.selectedOptions[0].value || '',
          sku: variant.sku || '',
        }
      })

      window.ShopifyAnalytics.meta.page.resourceId = this.product.id
      window.ShopifyAnalytics.meta.product = {
        gid: `gid://shopify/Product/${this.product.id}`,
        id: this.product.id,
        type: this.product.type,
        variants: currentVariants,
      }
    },

    subscriptionExpiryOptions() {
      this.expiryOptions = this.product.subscriptionExpiry?.value.split(',')
      if (this.expiryOptions) {
        this.purchaseType = 'temporary'
      }
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
